<template>
  <div class="courses-programmes-container container-md justify-content-center">

    <CModal color="primary" :show.sync="enrollModal.show" :close-on-backdrop="false">
      <template #header>
        <h6 class="modal-title">Confirm Enrollment</h6>
        <CButtonClose @click="enrollModal.show = false" class="text-black" />
      </template>
      <div class="p-3">
        <p>Are you sure you want to enroll in <strong>{{ enrollModal.courseName }}</strong>?</p>
      </div>
      <template #footer>
        <CButton color="secondary" @click="enrollModal.show = false">Cancel</CButton>
        <CButton color="primary" :disabled="enrollModal.loading" @click="confirmEnroll">
          <span v-if="enrollModal.loading"><CSpinner size="sm" /> Enrolling...</span>
          <span v-else>Yes, Enroll</span>
        </CButton>
      </template>
    </CModal>

    <div class="desktop-view">
      <div class="border col-lg-11 col-md-11 container-md justify-content-center mt-5">
        <CDataTable
          :striped="false" :bordered="false" :small="true"
          :items="enrichedCourses" :fields="displayFields"
          :items-per-page="10" :sorter="false" :tableFilter="false"
          pagination ref="offer-cand-tab"
        >
          <template #no-items-view>
            <h5 class="text-center">
              No Data Found <em class="fas fa-ban" :style="{ color: '#e55353' }"></em>
            </h5>
          </template>

          <template #course_title="{ item }">
            <td style="vertical-align: middle;">{{ item.course_title }}</td>
          </template>

          <template #course_type="{ item }">
            <td style="vertical-align: middle;">{{ item.course_type }}</td>
          </template>

          <template #delivery_type="{ item }">
            <td style="vertical-align: middle;">{{ item.delivery_type }}</td>
          </template>

          <template #progress="{ item }">
            <td style="vertical-align: middle;">
              <template v-if="item.isEnrolled">
                <CSpinner v-if="item.loadingProgress" size="sm" />
                <template v-else>
                  <CProgress
                    :value="item.progress || 0" size="sm"
                    :color="item.progress >= 100 ? 'success' : item.progress >= 50 ? 'warning' : 'danger'"
                  />
                  <span>{{ item.progress || 0 }}%</span>
                </template>
              </template>
              <span v-else class="text-muted">—</span>
            </td>
          </template>

          <template #action="{ item }">
            <td style="vertical-align: middle;">
              <CButton v-if="!item.isEnrolled" color="primary" square @click="openEnrollModal(item)">
                Enroll
              </CButton>
              <CButton
                v-else color="success" square
                :disabled="item.loadingSSO"
                @click="handleVisitCourse(item)"
              >
                <span v-if="item.loadingSSO"><CSpinner size="sm" /> Opening...</span>
                <span v-else>Visit Course</span>
              </CButton>
            </td>
          </template>
        </CDataTable>
      </div>
    </div>

    <div class="mobile-view">
      <div class="multi-records">
        <div
          class="top-border d-flex justify-content-between p-2"
          v-for="(item, index) in enrichedCourses" :key="index"
        >
          <CRow class="d-flex" style="justify-content: space-between; margin-left: 5px">
            <div class="table col col-5">
              <p class="head">Course Title</p>
              <p class="data">{{ item.course_title }}</p>
            </div>
            <div class="table col col-5">
              <p class="head">Course Type</p>
              <p class="data">{{ item.course_type }}</p>
            </div>
            <div class="table col col-5">
              <p class="head">Delivery Type</p>
              <p class="data">{{ item.delivery_type }}</p>
            </div>
            <div class="table col col-5">
              <p class="head">Progress</p>
              <p class="data">
                <template v-if="item.isEnrolled">
                  <CSpinner v-if="item.loadingProgress" size="sm" />
                  <template v-else>
                    <CProgress
                      :value="item.progress || 0" size="sm"
                      :color="item.progress >= 100 ? 'success' : item.progress >= 50 ? 'warning' : 'danger'"
                    />
                    {{ item.progress || 0 }}%
                  </template>
                </template>
                <span v-else>—</span>
              </p>
            </div>
            <div class="table col col-5">
              <CButton v-if="!item.isEnrolled" color="primary" square @click="openEnrollModal(item)">
                Enroll
              </CButton>
              <CButton
                v-else color="success" square
                :disabled="item.loadingSSO"
                @click="handleVisitCourse(item)"
              >
                <span v-if="item.loadingSSO">Opening...</span>
                <span v-else>Visit Course</span>
              </CButton>
            </div>
          </CRow>
        </div>
        <div v-if="!enrichedCourses.length">
          <p class="pt-3 text-center" :style="{ background: '#cccccc', height: '50px' }">
            No items <i class="fas fa-ban" :style="{ color: '#e55353' }"></i>
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "CoursesAndProgrammes",

  data() {
    return {
      displayFields: [
        { key: "course_title",  label: "Course Title",  _style: "width: 25%;" },
        { key: "course_type",   label: "Course Type",   _style: "width: 20%;" },
        { key: "delivery_type", label: "Delivery Type", _style: "width: 20%;" },
        { key: "progress",      label: "Progress",      _style: "width: 20%;" },
        { key: "action",        label: "Action",        _style: "width: 15%;" },
      ],
      courseUIState: {},
      enrollModal: {
        show:       false,
        loading:    false,
        courseItem: null,
        courseName: "",
      },
      progressMap: {},
    };
  },

  computed: {
    ...mapGetters([
      "courseList",
      "getCandidateEnrollments",
      "getCandidateId",
    ]),

    enrichedCourses() {
      return this.courseList.map(c => {
        const course             = c.training_course || c
        const training_course_id = course.training_course_id
        const enrollment         = this.getCandidateEnrollments.find(
          e => e.training_course_id === training_course_id
        )
        const progressData = this.progressMap[training_course_id] || {}

        return {
          ...course,
          isEnrolled:      !!enrollment,
          progress:        progressData.overall_pct ?? enrollment?.overall_progress ?? 0,
          has_content:     progressData.has_content ?? (enrollment?.overall_progress !== null && enrollment?.overall_progress !== undefined),
          loadingSSO:      this.courseUIState[training_course_id]?.loadingSSO      || false,
          loadingProgress: this.courseUIState[training_course_id]?.loadingProgress || false,
        }
      })
    }
  },

  async mounted() {
    await this.loadData()
  },

  methods: {
    ...mapActions([
      "getCourseList",
      "fetchCandidateEnrollments",
      "enrollCandidateInCourse",
      "fetchMoodleSSO",
      "fetchCourseProgress",
      "syncCourseProgress",
    ]),

    async loadData() {
      const candidate_uid = this.getCandidateId
      await Promise.all([
        this.getCourseList({ query: "moodle_synced=true" }),
        this.fetchCandidateEnrollments(candidate_uid),
      ])
      await this.loadAllProgress()
    },

    async loadAllProgress() {
      const enrolledCourses = this.courseList.filter(c => {
        const course = c.training_course || c
        return this.getCandidateEnrollments.some(
          e => e.training_course_id === course.training_course_id
        )
      })

      await Promise.all(enrolledCourses.map(async c => {
        const course = c.training_course || c
        const id     = course.training_course_id
        this.$set(this.courseUIState, id, { loadingProgress: true })
        const result = await this.fetchCourseProgress(id)
        this.$set(this.progressMap, id, {
          overall_pct: result?.overall_pct ?? 0,
          has_content: result?.has_content ?? false,
        })
        this.$set(this.courseUIState, id, { loadingProgress: false })
      }))
    },

    openEnrollModal(item) {
      this.enrollModal = {
        show:       true,
        loading:    false,
        courseItem: item,
        courseName: item.course_title,
      }
    },

    async confirmEnroll() {
      this.enrollModal.loading = true
      await this.enrollCandidateInCourse({
        candidate_uid:      this.getCandidateId,
        training_course_id: this.enrollModal.courseItem.training_course_id,
      })
      await this.fetchCandidateEnrollments(this.getCandidateId)
      await this.loadAllProgress()
      this.enrollModal.loading = false
      this.enrollModal.show    = false
    },

    async handleVisitCourse(item) {
      const id = item.training_course_id
      this.$set(this.courseUIState, id, { loadingSSO: true })
      const result = await this.fetchMoodleSSO(id)
      this.$set(this.courseUIState, id, { loadingSSO: false })

      if (result?.sso_url) {
        const moodleWindow = window.open(result.sso_url, '_blank')
        this.pollProgressAfterMoodle(id, moodleWindow)
      }
    },

    pollProgressAfterMoodle(id, moodleWindow) {
      const interval = setInterval(async () => {
        if (moodleWindow && moodleWindow.closed) {
          clearInterval(interval)
          this.$set(this.courseUIState, id, { loadingProgress: true })
          const updated = await this.syncCourseProgress(id)
          this.$set(this.progressMap, id, {
            overall_pct: updated?.overall_pct ?? 0,
            has_content: updated?.has_content ?? false,
          })
          this.$set(this.courseUIState, id, { loadingProgress: false })
        }
      }, 5000)
    },
  },
};
</script>

<style lang="scss" scoped>
.mobile-view {
  @media (min-width: 900px) { display: none; }
}
.desktop-view {
  @media (max-width: 900px) { display: none; }
}
td { vertical-align: middle; }
</style>